import { personLabel } from "./people";

/**
 * `@Display Name` matching for the comment composer and rendered comments.
 * Keep the matching rules in step with `packages/core/src/issues/mentions.ts`.
 */

export interface MentionPerson {
  id: string;
  displayName: string;
}

export interface MentionHit {
  ids: string[];
  start: number;
  end: number;
}

/** The `@` query in progress at the caret, when the user is mentioning someone. */
export function mentionQuery(value: string, caret: number): { start: number; query: string } | null {
  const before = value.slice(0, Math.max(0, caret));
  const at = before.lastIndexOf("@");
  if (at < 0) return null;
  const previous = at > 0 ? before[at - 1] : undefined;
  if (previous !== undefined && !/\s/.test(previous)) return null;
  const query = before.slice(at + 1);
  if (query.length > 80 || /[\n\r]/.test(query) || /\s{2}|\s$/.test(query)) return null;
  return { start: at, query };
}

export function filterMentionUsers<
  T extends MentionPerson & { email: string; kind: string; active: boolean },
>(users: T[], query: string): T[] {
  const needle = query.trim().toLowerCase();
  return users
    .filter((user) => user.kind === "human" && user.active && personLabel(user) !== "Someone")
    .map((user) => ({ user, score: scoreMention(user, needle) }))
    .filter((row) => row.score > 0)
    .sort(
      (a, b) =>
        b.score - a.score ||
        personLabel(a.user).localeCompare(personLabel(b.user), undefined, { sensitivity: "base" }),
    )
    .slice(0, 8)
    .map((row) => row.user);
}

function scoreMention(user: MentionPerson & { email: string }, needle: string): number {
  if (!needle) return 1;
  const name = personLabel(user).toLowerCase();
  const email = user.email.toLowerCase();
  const local = email.split("@")[0] ?? "";
  if (name.startsWith(needle)) return 4;
  if (name.split(/\s+/).some((part) => part.startsWith(needle))) return 3;
  if (local.startsWith(needle)) return 2;
  if (name.includes(needle) || email.includes(needle)) return 1;
  return 0;
}

function isBoundary(char: string | undefined): boolean {
  if (char === undefined) return true;
  return /[\s.,!?;:)\]}'"”’]/.test(char);
}

export function mentionHits(body: string, people: MentionPerson[]): MentionHit[] {
  const usable = people
    .map((person) => ({ id: person.id, name: person.displayName.trim() }))
    .filter((person) => person.name.length > 0 && !/[[\]()@\n\r]/.test(person.name));
  usable.sort((a, b) => b.name.length - a.name.length || a.id.localeCompare(b.id));

  const hits: MentionHit[] = [];
  let index = 0;
  while (index < body.length) {
    const previous = index > 0 ? body[index - 1] : undefined;
    if (body[index] !== "@" || (previous !== undefined && !/\s/.test(previous))) {
      index += 1;
      continue;
    }
    const rest = body.slice(index + 1);
    const match = usable.find(
      (person) =>
        rest.toLowerCase().startsWith(person.name.toLowerCase()) && isBoundary(rest[person.name.length]),
    );
    if (!match) {
      index += 1;
      continue;
    }
    const name = match.name.toLowerCase();
    const ids = usable.filter((person) => person.name.toLowerCase() === name).map((person) => person.id);
    const end = index + 1 + match.name.length;
    hits.push({ ids, start: index, end });
    index = end;
  }
  return hits;
}

/** Turns `@Name` into a `mention:` link so rendered comments can highlight it. */
export function linkMentions(body: string, people: MentionPerson[]): string {
  const hits = mentionHits(body, people);
  let next = body;
  for (const hit of [...hits].reverse()) {
    const id = hit.ids[0];
    if (!id) continue;
    const label = body.slice(hit.start, hit.end).replace(/[[\]]/g, "");
    next = `${next.slice(0, hit.start)}[${label}](mention:${id})${next.slice(hit.end)}`;
  }
  return next;
}

/** Allows the mention protocol through and drops anything the browser should not open. */
export function mentionUrlTransform(url: string): string {
  if (url.startsWith("mention:")) return url;
  const colon = url.indexOf(":");
  if (colon === -1) return url;
  const protocol = url.slice(0, colon);
  if (/^(https?|ircs?|mailto|xmpp)$/i.test(protocol)) return url;
  return "";
}
